<template>
  <div class="tuition-stat-v3">
    <!-- 1. Header khu vực Thống kê học phí & Bộ lọc -->
    <div class="stat-section-header">
      <div class="header-left">
        <div class="section-icon-badge">
          <i class="el-icon-wallet"></i>
        </div>
        <div>
          <div class="title-with-pill">
            <h2 class="section-main-title">Thống Kê Học Phí & Khoản Thu</h2>
            <span class="live-status-pill">
              <span class="pulse-dot"></span> Dữ liệu học phí
            </span>
          </div>
          <p class="section-subtitle">
            Theo dõi tổng nguồn thu, tiến độ đóng học phí và phân loại 7 trạng thái phiếu thu của từng lớp
          </p>
        </div>
      </div>

      <!-- Bộ lọc chung: Theo tháng / Theo khoảng thời gian + Khối -->
      <div class="header-filters">
        <!-- Switcher mode: Theo tháng vs Theo khoảng thời gian -->
        <div class="filter-mode-tabs">
          <button
            type="button"
            :class="['mode-tab-btn', { active: filterMode === 'month' }]"
            @click="setFilterMode('month')"
          >
            <i class="el-icon-date"></i> Theo Tháng
          </button>
          <button
            type="button"
            :class="['mode-tab-btn', { active: filterMode === 'range' }]"
            @click="setFilterMode('range')"
          >
            <i class="el-icon-time"></i> Theo Khoảng Thời Gian
          </button>
        </div>

        <!-- Date picker theo mode -->
        <div class="picker-container">
          <!-- Mode 1: Theo tháng -->
          <el-date-picker
            v-if="filterMode === 'month'"
            key="tuition-month-picker"
            v-model="selectedMonth"
            type="month"
            size="small"
            format="MM/yyyy"
            value-format="yyyy-MM"
            placeholder="Chọn tháng"
            :clearable="false"
            class="month-picker"
            @change="handleFilterChange"
          />

          <!-- Mode 2: Theo khoảng thời gian -->
          <el-date-picker
            v-else
            key="tuition-month-range-picker"
            v-model="selectedDateRange"
            type="monthrange"
            size="small"
            format="MM/yyyy"
            value-format="yyyy-MM"
            range-separator="-"
            start-placeholder="Từ tháng"
            end-placeholder="Đến tháng"
            :clearable="false"
            class="range-picker"
            @change="handleFilterChange"
          />
        </div>

        <!-- Bộ lọc Khối -->
        <div class="grade-filter-box">
          <el-select
            v-model="selectedGrade"
            size="small"
            placeholder="Tất cả các khối"
            clearable
            class="grade-select"
            @change="handleGradeChange"
          >
            <el-option
              v-for="g in gradeOptions"
              :key="g.value"
              :label="g.label"
              :value="g.value"
            />
          </el-select>
        </div>
      </div>
    </div>

    <el-alert
      v-if="loading"
      title="Đang tải thống kê học phí..."
      type="info"
      :closable="false"
      show-icon
      class="tuition-state-alert"
    />
    <el-alert
      v-else-if="errorMessage"
      :title="errorMessage"
      type="error"
      :closable="false"
      show-icon
      class="tuition-state-alert"
    />
    <el-alert
      v-else-if="noData"
      title="Không có dữ liệu học phí trong kỳ đã chọn."
      type="info"
      :closable="false"
      show-icon
      class="tuition-state-alert"
    />

    <!-- 2. 3 MỤC CHỈ SỐ CHÍNH: Tổng phải thu, Tổng đã thu, Tổng còn lại -->
    <template v-if="!loading && !errorMessage && !noData">
    <div class="tuition-kpi-grid">
      <!-- KPI 1: Tổng Phải Thu -->
      <div class="tuition-kpi-card kpi-receivable">
        <div class="kpi-icon-wrap bg-blue-subtle text-blue">
          <i class="el-icon-wallet"></i>
        </div>
        <div class="kpi-body">
          <div class="kpi-top-row">
            <span class="kpi-label">Tổng Phải Thu</span>
            <span class="kpi-tag tag-blue">Kế hoạch</span>
          </div>
          <div class="kpi-value-row">
            <strong class="kpi-amount text-blue-dark">{{ formatMoney(selectedSummary.totalReceivable) }}</strong>
          </div>
          <div class="kpi-meta-text">
            <span><i class="el-icon-document"></i> <b>{{ selectedSummary.kidsTotalNumber }}</b> học sinh trong roster kỳ</span>
          </div>
        </div>
      </div>

      <!-- KPI 2: Tổng Đã Thu -->
      <div class="tuition-kpi-card kpi-paid">
        <div class="kpi-icon-wrap bg-emerald-subtle text-emerald">
          <i class="el-icon-circle-check"></i>
        </div>
        <div class="kpi-body">
          <div class="kpi-top-row">
            <span class="kpi-label">Tổng Đã Thu</span>
            <span class="kpi-tag tag-emerald">Đạt {{ collectionRate }}%</span>
          </div>
          <div class="kpi-value-row">
            <strong class="kpi-amount text-emerald">{{ formatMoney(selectedSummary.totalPaid) }}</strong>
          </div>
          <div class="kpi-progress-bar">
            <div class="kpi-progress-fill fill-emerald" :style="{ width: collectionProgressWidth + '%' }"></div>
          </div>
          <div class="kpi-meta-text">
            <span><i class="el-icon-check"></i> <b>{{ selectedSummary.completedStudents }}</b> học sinh đã hoàn tất đóng phí</span>
          </div>
        </div>
      </div>

      <!-- KPI 3: Tổng Còn Lại -->
      <div class="tuition-kpi-card kpi-remaining">
        <div class="kpi-icon-wrap bg-rose-subtle text-rose">
          <i class="el-icon-warning-outline"></i>
        </div>
        <div class="kpi-body">
          <div class="kpi-top-row">
            <span class="kpi-label">Tổng Còn Lại (Công nợ)</span>
            <span class="kpi-tag tag-rose">Còn {{ remainingRate }}%</span>
          </div>
          <div class="kpi-value-row">
            <strong class="kpi-amount text-rose">{{ formatMoney(selectedSummary.totalRemaining) }}</strong>
          </div>
          <div class="kpi-progress-bar">
            <div class="kpi-progress-fill fill-rose" :style="{ width: remainingProgressWidth + '%' }"></div>
          </div>
          <div class="kpi-meta-text">
            <span><i class="el-icon-time"></i> <b>{{ selectedSummary.pendingStudents }}</b> học sinh cần đôn đốc nộp bổ sung</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 3. THỐNG KÊ NHANH 7 TRẠNG THÁI PHIẾU THU HỌC SINH -->
    <div class="status-summary-strip">
      <div class="strip-header">
        <span class="strip-title">
          <i class="el-icon-pie-chart"></i> Phân Bổ 7 Trạng Thái Đóng Học Phí
        </span>
        <span class="strip-count-text">
          Tổng số học sinh xét duyệt: <b>{{ selectedSummary.kidsTotalNumber }} HS</b>
        </span>
      </div>

      <div class="status-chips-grid">
        <!-- 1. Chưa có phiếu thu -->
        <div class="status-chip-card chip-unbilled">
          <div class="chip-top">
            <span class="status-dot dot-gray"></span>
            <span class="status-name">Chưa có phiếu thu</span>
          </div>
          <div class="chip-count">
            <b>{{ selectedSummary.unbilled }}</b> <span class="unit">HS</span>
            <span class="pct">({{ selectedSummary.unbilledRate }}%)</span>
          </div>
        </div>

        <!-- 2. Chưa đóng 100% -->
        <div class="status-chip-card chip-unpaid">
          <div class="chip-top">
            <span class="status-dot dot-rose"></span>
            <span class="status-name">Chưa đóng 100%</span>
          </div>
          <div class="chip-count">
            <b class="text-rose">{{ selectedSummary.unpaid }}</b> <span class="unit">HS</span>
            <span class="pct">({{ selectedSummary.unpaidRate }}%)</span>
          </div>
        </div>

        <!-- 3. Chưa đóng đủ -->
        <div class="status-chip-card chip-partial">
          <div class="chip-top">
            <span class="status-dot dot-amber"></span>
            <span class="status-name">Chưa đóng đủ</span>
          </div>
          <div class="chip-count">
            <b class="text-amber">{{ selectedSummary.partiallyPaid }}</b> <span class="unit">HS</span>
            <span class="pct">({{ selectedSummary.partiallyPaidRate }}%)</span>
          </div>
        </div>

        <!-- 4. Đóng đủ 100% -->
        <div class="status-chip-card chip-full">
          <div class="chip-top">
            <span class="status-dot dot-emerald"></span>
            <span class="status-name">Đóng đủ 100%</span>
          </div>
          <div class="chip-count">
            <b class="text-emerald">{{ selectedSummary.fullyPaid }}</b> <span class="unit">HS</span>
            <span class="pct">({{ selectedSummary.fullyPaidRate }}%)</span>
          </div>
        </div>

        <!-- 5. Đóng thừa -->
        <div class="status-chip-card chip-overpaid">
          <div class="chip-top">
            <span class="status-dot dot-purple"></span>
            <span class="status-name">Đóng thừa</span>
          </div>
          <div class="chip-count">
            <b class="text-purple">{{ selectedSummary.overpaid }}</b> <span class="unit">HS</span>
            <span class="pct">({{ selectedSummary.overpaidRate }}%)</span>
          </div>
        </div>

        <!-- 6. Đóng đủ thừa -->
        <div class="status-chip-card chip-surplus">
          <div class="chip-top">
            <span class="status-dot dot-teal"></span>
            <span class="status-name">Đóng đủ thừa</span>
          </div>
          <div class="chip-count">
            <b class="text-teal">{{ selectedSummary.fullyPaidWithSurplus }}</b> <span class="unit">HS</span>
            <span class="pct">({{ selectedSummary.fullyPaidWithSurplusRate }}%)</span>
          </div>
        </div>

        <!-- 7. Đóng đủ thiếu -->
        <div class="status-chip-card chip-deficit">
          <div class="chip-top">
            <span class="status-dot dot-indigo"></span>
            <span class="status-name">Đóng đủ thiếu</span>
          </div>
          <div class="chip-count">
            <b class="text-indigo">{{ selectedSummary.paidWithDeficit }}</b> <span class="unit">HS</span>
            <span class="pct">({{ selectedSummary.paidWithDeficitRate }}%)</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 4. KHU VỰC BIỂU ĐỒ TRỰC QUAN (2 Biểu đồ) -->
    <el-row :gutter="20" class="charts-row">
      <!-- Biểu đồ 1: Tiến độ thu học phí theo khối lớp -->
      <el-col :xs="24" :sm="24" :md="14" :lg="14">
        <div class="chart-card h-100">
          <div class="chart-card-head">
            <div>
              <h3 class="chart-title">Tiến Độ Thu Học Phí Theo Khối Lớp</h3>
              <p class="chart-subtitle">So sánh số tiền thực tế đã thu và công nợ còn lại theo khối</p>
            </div>
            <div class="tuition-chart-legend">
              <span class="tuition-legend-item"><span class="tuition-legend-color tuition-legend-color--paid"></span> Đã thu</span>
              <span class="tuition-legend-item"><span class="tuition-legend-color tuition-legend-color--remaining"></span> Còn lại</span>
            </div>
          </div>
          <div class="chart-container" ref="chartTuitionGradeProgress"></div>
        </div>
      </el-col>

      <!-- Biểu đồ 2: Cơ cấu 7 trạng thái đóng phí học sinh -->
      <el-col :xs="24" :sm="24" :md="10" :lg="10">
        <div class="chart-card h-100">
          <div class="chart-card-head">
            <div>
              <h3 class="chart-title">Cơ Cấu Trạng Thái Phiếu Thu</h3>
              <p class="chart-subtitle">Tỷ trọng 7 trạng thái đóng học phí trong phạm vi khối đã chọn</p>
            </div>
            <span class="badge-soft badge-soft--blue">7 trạng thái</span>
          </div>
          <div class="chart-container" ref="chartTuitionStatusDonut"></div>
        </div>
      </el-col>
    </el-row>

    <!-- 5. BẢNG THỐNG KÊ CÁC LỚP THEO TRẠNG THÁI TỪNG LỚP TRONG KHỐI -->
    <div class="class-tuition-section">
      <div :class="['class-tuition-head', { 'is-expanded': showClassTable }]">
        <div class="class-tuition-title-group">
          <div class="section-icon-badge sub-icon">
            <i class="el-icon-s-grid"></i>
          </div>
          <div>
            <h3 class="section-heading">Thống Kê Chỉ Số Đóng Học Phí Theo Từng Lớp</h3>
            <p class="section-subheading">
              Chi tiết số lượng học sinh theo 7 trạng thái thu học phí và số tiền phải thu, đã thu, còn lại
            </p>
          </div>
        </div>

        <div class="class-tuition-actions">
          <span class="counter-badge">
            <i class="el-icon-school"></i> {{ filteredClassList.length }} Lớp
          </span>
          <el-button
            size="small"
            type="primary"
            plain
            class="table-toggle-btn"
            @click="showClassTable = !showClassTable"
          >
            <i :class="showClassTable ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
            {{ showClassTable ? 'Thu gọn bảng' : 'Xem chi tiết bảng' }}
          </el-button>
        </div>
      </div>

      <el-collapse-transition>
        <div v-show="showClassTable" class="class-tuition-collapsible-body">
          <div class="class-tuition-search-wrap">
            <el-input
              v-model="tableSearchQuery"
              size="small"
              placeholder="Tìm theo tên lớp..."
              prefix-icon="el-icon-search"
              clearable
              class="table-search-input"
            />
          </div>

          <!-- Bảng thống kê trạng thái đóng phí từng lớp (GIỚI HẠN CHIỀU CAO <= 500px, STICKY HEADER) -->
          <div class="class-tuition-table-wrap">
            <el-table
          :data="filteredClassList"
          stripe
          border
          size="small"
          max-height="500"
          class="modern-tuition-table"
          empty-text="Không tìm thấy lớp học nào phù hợp"
          show-summary
          :summary-method="getCustomTableSummaries"
        >
          <el-table-column type="index" label="STT" width="50" align="center" fixed="left" />

          <!-- Tên Lớp & Khối -->
          <el-table-column label="Lớp Học & Khối" min-width="160" fixed="left">
            <template slot-scope="scope">
              <div class="class-cell-meta">
                <strong class="class-name-txt">{{ scope.row.className }}</strong>
                <span class="grade-pill" :class="'pill-' + scope.row.gradeId">{{ scope.row.gradeName }}</span>
              </div>
            </template>
          </el-table-column>

          <!-- Sĩ Số -->
          <el-table-column label="Sĩ Số" prop="kidsTotalNumber" width="75" align="center">
            <template slot-scope="scope">
              <span class="student-count-badge">{{ scope.row.kidsTotalNumber }}</span>
            </template>
          </el-table-column>

          <!-- 1. Chưa có phiếu thu -->
          <el-table-column label="Chưa Có Phiếu" prop="unbilled" width="105" align="center">
            <template slot-scope="scope">
              <span :class="scope.row.unbilled > 0 ? 'badge-st badge-unbilled' : 'badge-st badge-zero'">
                {{ scope.row.unbilled }}
              </span>
            </template>
          </el-table-column>

          <!-- 2. Chưa đóng 100% -->
          <el-table-column label="Chưa Đóng 100%" prop="unpaid" width="115" align="center">
            <template slot-scope="scope">
              <span :class="scope.row.unpaid > 0 ? 'badge-st badge-unpaid' : 'badge-st badge-zero'">
                {{ scope.row.unpaid }}
              </span>
            </template>
          </el-table-column>

          <!-- 3. Chưa đóng đủ -->
          <el-table-column label="Chưa Đóng Đủ" prop="partiallyPaid" width="110" align="center">
            <template slot-scope="scope">
              <span :class="scope.row.partiallyPaid > 0 ? 'badge-st badge-partial' : 'badge-st badge-zero'">
                {{ scope.row.partiallyPaid }}
              </span>
            </template>
          </el-table-column>

          <!-- 4. Đóng đủ 100% -->
          <el-table-column label="Đóng Đủ 100%" prop="fullyPaid" width="115" align="center">
            <template slot-scope="scope">
              <span class="badge-st badge-fully-paid">
                {{ scope.row.fullyPaid }}
              </span>
            </template>
          </el-table-column>

          <!-- 5. Đóng thừa -->
          <el-table-column label="Đóng Thừa" prop="overpaid" width="95" align="center">
            <template slot-scope="scope">
              <span :class="scope.row.overpaid > 0 ? 'badge-st badge-overpaid' : 'badge-st badge-zero'">
                {{ scope.row.overpaid }}
              </span>
            </template>
          </el-table-column>

          <!-- 6. Đóng đủ thừa -->
          <el-table-column label="Đóng Đủ Thừa" prop="fullyPaidWithSurplus" width="110" align="center">
            <template slot-scope="scope">
              <span :class="scope.row.fullyPaidWithSurplus > 0 ? 'badge-st badge-surplus' : 'badge-st badge-zero'">
                {{ scope.row.fullyPaidWithSurplus }}
              </span>
            </template>
          </el-table-column>

          <!-- 7. Đóng đủ thiếu -->
          <el-table-column label="Đóng Đủ Thiếu" prop="paidWithDeficit" width="110" align="center">
            <template slot-scope="scope">
              <span :class="scope.row.paidWithDeficit > 0 ? 'badge-st badge-deficit' : 'badge-st badge-zero'">
                {{ scope.row.paidWithDeficit }}
              </span>
            </template>
          </el-table-column>

          <!-- Tổng phải thu -->
          <el-table-column label="Phải Thu" prop="totalReceivable" min-width="125" align="right">
            <template slot-scope="scope">
              <span class="money-cell text-slate-dark">{{ formatMoney(scope.row.totalReceivable) }}</span>
            </template>
          </el-table-column>

          <!-- Tổng đã thu -->
          <el-table-column label="Đã Thu" prop="totalPaid" min-width="125" align="right">
            <template slot-scope="scope">
              <strong class="money-cell text-emerald">{{ formatMoney(scope.row.totalPaid) }}</strong>
            </template>
          </el-table-column>

          <!-- Tổng còn lại -->
          <el-table-column label="Còn Lại" prop="totalRemaining" min-width="120" align="right">
            <template slot-scope="scope">
              <strong class="money-cell text-rose">{{ formatMoney(scope.row.totalRemaining) }}</strong>
            </template>
          </el-table-column>
        </el-table>
      </div>
        </div>
      </el-collapse-transition>
    </div>
    </template>
  </div>
</template>

<script>
import * as echarts from "echarts";
import TuitionStatisticsService from "@/services/dashboard_v3/TuitionStatisticsService";

function currentMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

function currentMonthRange() {
  const month = currentMonth();
  return [month, month];
}

export default {
  name: "TuitionStatisticsV3",
  props: {
    autoLoad: {
      type: Boolean,
      default: true
    }
  },
  data() {
    const month = currentMonth();
    return {
      hasLoadedData: false,
      isLoadingData: false,
      showClassTable: false,
      filterMode: "month",
      selectedMonth: month,
      selectedDateRange: currentMonthRange(),
      selectedGrade: "",
      tableSearchQuery: "",
      classList: [],
      gradeSummaries: [],
      summaryData: null,
      loading: false,
      errorMessage: "",
      latestRequestId: 0,
      chartProgress: null,
      chartStatus: null
    };
  },
  computed: {
    gradeOptions() {
      return [
        { label: "Tất cả các khối", value: "" },
        ...this.gradeSummaries.map(grade => ({ value: String(grade.gradeId), label: grade.gradeName }))
      ];
    },
    filteredClassList() {
      let list = this.classList;
      if (this.selectedGrade) {
        list = list.filter(row => String(row.gradeId) === String(this.selectedGrade));
      }
      const query = this.tableSearchQuery.toLowerCase().trim();
      if (query) {
        list = list.filter(row =>
          (row.className || "").toLowerCase().includes(query) ||
          (row.gradeName || "").toLowerCase().includes(query)
        );
      }
      return list;
    },
    selectedSummary() {
      if (this.selectedGrade) {
        return this.gradeSummaries.find(grade => String(grade.gradeId) === String(this.selectedGrade)) || this.summaryData;
      }
      return this.summaryData;
    },
    noData() {
      if (!this.classList.length || !this.selectedSummary) return true;
      return Number(this.selectedSummary.kidsTotalNumber || 0) === 0 &&
        Number(this.selectedSummary.totalReceivable || 0) === 0 &&
        Number(this.selectedSummary.totalPaid || 0) === 0;
    },
    collectionRate() {
      return Number(this.selectedSummary && this.selectedSummary.collectionRate || 0).toFixed(1);
    },
    remainingRate() {
      return Number(this.selectedSummary && this.selectedSummary.remainingRate || 0).toFixed(1);
    },
    collectionProgressWidth() {
      return Math.max(0, Math.min(100, Number(this.collectionRate)));
    },
    remainingProgressWidth() {
      return Math.max(0, Math.min(100, Number(this.remainingRate)));
    }
  },
  mounted() {
    window.addEventListener("resize", this.handleResize);
    if (this.autoLoad) {
      this.loadData();
    }
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
    this.disposeCharts();
  },
  methods: {
    async loadData() {
      if (this.hasLoadedData || this.isLoadingData) return;
      this.isLoadingData = true;
      try {
        await this.loadStatistics();
        this.hasLoadedData = true;
      } finally {
        this.isLoadingData = false;
      }
    },
    setFilterMode(mode) {
      this.filterMode = mode;
      if (mode === "range" && !this.selectedDateRange) this.selectedDateRange = currentMonthRange();
      if (mode === "month" && !this.selectedMonth) this.selectedMonth = currentMonth();
      this.loadStatistics();
    },
    handleFilterChange() {
      this.loadStatistics();
    },
    handleGradeChange() {
      this.updateCharts();
    },
    requestMonths() {
      if (this.filterMode === "month") {
        return { startMonth: this.selectedMonth, endMonth: this.selectedMonth };
      }
      if (!Array.isArray(this.selectedDateRange) || this.selectedDateRange.length !== 2) return null;
      return {
        startMonth: this.selectedDateRange[0],
        endMonth: this.selectedDateRange[1]
      };
    },
    async loadStatistics() {
      const months = this.requestMonths();
      if (!months || !months.startMonth || !months.endMonth) return;
      const requestId = ++this.latestRequestId;
      this.disposeCharts();
      this.loading = true;
      this.errorMessage = "";
      try {
        const response = await TuitionStatisticsService.getSummary(months.startMonth, months.endMonth);
        if (requestId !== this.latestRequestId) return;
        const data = response && response.data && response.data.data;
        if (!data || !data.summary || !Array.isArray(data.grades) || !Array.isArray(data.classes)) {
          throw new Error("BE trả dữ liệu thống kê học phí không hợp lệ.");
        }
        this.summaryData = data.summary;
        this.gradeSummaries = data.grades;
        this.classList = data.classes;
        if (this.selectedGrade && !data.grades.some(grade => String(grade.gradeId) === String(this.selectedGrade))) {
          this.selectedGrade = "";
        }
        this.loading = false;
        this.$nextTick(() => this.initCharts());
      } catch (error) {
        if (requestId !== this.latestRequestId) return;
        this.classList = [];
        this.gradeSummaries = [];
        this.summaryData = null;
        this.loading = false;
        this.errorMessage = (error && error.response && error.response.data &&
          (error.response.data.message || error.response.data.error)) ||
          error.message || "Không tải được thống kê học phí. Vui lòng thử lại.";
        this.disposeCharts();
      }
    },
    formatMoney(value) {
      const amount = Number(value || 0);
      if (!Number.isFinite(amount)) return "0 ₫";
      return `${Math.round(amount).toLocaleString("vi-VN")} ₫`;
    },
    handleResize() {
      if (this.chartProgress) this.chartProgress.resize();
      if (this.chartStatus) this.chartStatus.resize();
    },
    disposeCharts() {
      if (this.chartProgress) {
        this.chartProgress.dispose();
        this.chartProgress = null;
      }
      if (this.chartStatus) {
        this.chartStatus.dispose();
        this.chartStatus = null;
      }
    },
    initCharts() {
      this.renderProgressChart();
      this.renderStatusChart();
    },
    updateCharts() {
      this.$nextTick(this.initCharts);
    },
    renderProgressChart() {
      const dom = this.$refs.chartTuitionGradeProgress;
      if (!dom) return;
      if (!this.chartProgress) this.chartProgress = echarts.init(dom);

      const grades = this.gradeSummaries.filter(grade =>
        !this.selectedGrade || String(grade.gradeId) === String(this.selectedGrade)
      );
      const maxGradeLabelLength = 14;
      const shortGradeName = name => {
        const value = String(name || "");
        return value.length > maxGradeLabelLength
          ? `${value.slice(0, maxGradeLabelLength - 3)}...` : value;
      };
      const moneyInMillions = value => Number(value || 0) / 1000000;
      this.chartProgress.setOption({
        tooltip: {
          trigger: "axis", axisPointer: { type: "shadow" },
          backgroundColor: "rgba(15, 23, 42, 0.9)", textStyle: { color: "#fff" },
          formatter: params => {
            const grade = params.length ? grades[params[0].dataIndex] : null;
            return grade ? `<b>${grade.gradeName}</b><br/>Đã thu: ${this.formatMoney(grade.totalPaid)}<br/>Còn lại: ${this.formatMoney(grade.totalRemaining)}` : "";
          }
        },
        legend: { show: false },
        grid: { top: 30, left: 10, right: 20, bottom: 10, containLabel: true },
        xAxis: { type: "category", data: grades.map(grade => grade.gradeName),
          axisLine: { lineStyle: { color: "#e2e8f0" } }, axisTick: { show: false },
          axisLabel: { interval: 0, width: 100, overflow: "truncate", color: "#475569",
            fontWeight: 600, fontSize: 12, formatter: shortGradeName } },
        yAxis: { type: "value", name: "Triệu đồng", nameTextStyle: { color: "#94a3b8", fontSize: 11 },
          splitLine: { lineStyle: { color: "#f1f5f9" } }, axisLabel: { color: "#94a3b8", formatter: "{value} tr" } },
        series: [
          { name: "Đã thu", type: "bar", barWidth: 20, barGap: "20%",
          data: grades.map(grade => moneyInMillions(Number(grade.totalPaid || 0))),
            itemStyle: { color: new echarts.graphic.LinearGradient(0, 0, 0, 1,
              [{ offset: 0, color: "#10b981" }, { offset: 1, color: "#059669" }]), borderRadius: [6, 6, 0, 0] } },
          { name: "Còn lại", type: "bar", barWidth: 20, barGap: "20%",
            data: grades.map(grade => moneyInMillions(Number(grade.totalRemaining || 0))),
            itemStyle: { color: new echarts.graphic.LinearGradient(0, 0, 0, 1,
              [{ offset: 0, color: "#f43f5e" }, { offset: 1, color: "#e11d48" }]), borderRadius: [6, 6, 0, 0] } }
        ]
      }, true);
    },
    renderStatusChart() {
      const dom = this.$refs.chartTuitionStatusDonut;
      if (!dom) return;
      if (!this.chartStatus) this.chartStatus = echarts.init(dom);
      const totals = this.selectedSummary;
      const statusData = [
        { value: totals.fullyPaid, rate: totals.fullyPaidRate, name: "Đóng đủ 100%", itemStyle: { color: "#10b981" } },
        { value: totals.fullyPaidWithSurplus, rate: totals.fullyPaidWithSurplusRate, name: "Đóng đủ thừa", itemStyle: { color: "#06b6d4" } },
        { value: totals.overpaid, rate: totals.overpaidRate, name: "Đóng thừa", itemStyle: { color: "#a855f7" } },
        { value: totals.paidWithDeficit, rate: totals.paidWithDeficitRate, name: "Đóng đủ thiếu", itemStyle: { color: "#6366f1" } },
        { value: totals.partiallyPaid, rate: totals.partiallyPaidRate, name: "Chưa đóng đủ", itemStyle: { color: "#f59e0b" } },
        { value: totals.unpaid, rate: totals.unpaidRate, name: "Chưa đóng 100%", itemStyle: { color: "#f43f5e" } },
        { value: totals.unbilled, rate: totals.unbilledRate, name: "Chưa có phiếu", itemStyle: { color: "#94a3b8" } }
      ];
      this.chartStatus.setOption({
        tooltip: { trigger: "item", backgroundColor: "rgba(15, 23, 42, 0.9)", textStyle: { color: "#fff" },
          formatter: params => `${params.name}: <b>${params.value} HS</b> (${params.data.rate}%)` },
        legend: { orient: "vertical", right: 5, top: "middle", itemWidth: 10, itemHeight: 10,
          textStyle: { fontSize: 11, color: "#475569" } },
        series: [{ name: "Trạng thái", type: "pie", radius: ["48%", "72%"], center: ["38%", "50%"],
          avoidLabelOverlap: false, itemStyle: { borderRadius: 6, borderColor: "#fff", borderWidth: 2 },
          label: { show: false }, emphasis: { label: { show: true, fontSize: 14, fontWeight: "bold",
            formatter: params => `${params.name}\\n${params.data.rate}%` } },
          labelLine: { show: false }, data: statusData }]
      }, true);
    },
    getCustomTableSummaries({ columns, data }) {
      const totals = data.reduce((result, row) => {
        result.students += Number(row.kidsTotalNumber || 0);
        return result;
      }, { students: 0 });
      return columns.map((column, index) => {
        if (index === 0) return "Tổng";
        if (index === 1) return `${data.length} Lớp học`;
        if (index === 2) return `${totals.students} HS`;
        const key = column.property;
        if (!key) return "";
        const total = data.reduce((sum, row) => sum + Number(row[key] || 0), 0);
        if (["totalReceivable", "totalPaid", "totalRemaining"].includes(key)) return this.formatMoney(total);
        return total;
      });
    }
  }
};
</script>

<style scoped>
.tuition-state-alert {
  margin-bottom: 18px;
}

.tuition-stat-v3 {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  padding: 24px;
  box-shadow: 0 4px 20px -3px rgba(0, 0, 0, 0.04);
  margin-bottom: 28px;
}

/* Header */
.stat-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 1px solid #f1f5f9;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.section-icon-badge {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: #eff6ff;
  color: #2563eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  flex-shrink: 0;
}

.section-icon-badge.sub-icon {
  width: 38px;
  height: 38px;
  font-size: 20px;
  border-radius: 10px;
  background: #f1f5f9;
  color: #475569;
}

.title-with-pill {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.section-main-title {
  margin: 0;
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
}

.live-status-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: #ecfdf5;
  color: #059669;
  border: 1px solid #a7f3d0;
  border-radius: 999px;
  font-size: 11.5px;
  font-weight: 700;
}

.pulse-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #10b981;
  box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.3);
}

.section-subtitle {
  margin: 4px 0 0;
  font-size: 13px;
  color: #64748b;
}

/* Header Filters */
.header-filters {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.filter-mode-tabs {
  display: flex;
  background: #f1f5f9;
  padding: 3px;
  border-radius: 10px;
}

.mode-tab-btn {
  border: none;
  background: transparent;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  transition: all 0.2s ease;
}

.mode-tab-btn.active {
  background: #ffffff;
  color: #2563eb;
  font-weight: 700;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
}

.picker-container .month-picker {
  width: 165px;
}

.picker-container .range-picker {
  width: 240px;
}

.grade-filter-box .grade-select {
  width: 190px;
}

/* 2. 3 MỤC CHỈ SỐ CHÍNH (KPI Cards) */
.tuition-kpi-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 18px;
  margin-bottom: 22px;
}

.tuition-kpi-card {
  display: flex;
  gap: 16px;
  padding: 20px;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.tuition-kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px -4px rgba(0, 0, 0, 0.06);
}

.kpi-receivable {
  border-left: 5px solid #3b82f6;
}

.kpi-paid {
  border-left: 5px solid #10b981;
}

.kpi-remaining {
  border-left: 5px solid #f43f5e;
}

.kpi-icon-wrap {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
  flex-shrink: 0;
}

.bg-blue-subtle { background: #eff6ff; }
.text-blue { color: #2563eb; }
.text-blue-dark { color: #1e3a8a; }

.bg-emerald-subtle { background: #ecfdf5; }
.text-emerald { color: #059669; }

.bg-rose-subtle { background: #fff1f2; }
.text-rose { color: #e11d48; }

.kpi-body {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.kpi-top-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

.kpi-label {
  font-size: 13px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.kpi-tag {
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 6px;
}

.tag-blue { background: #dbeafe; color: #1d4ed8; }
.tag-emerald { background: #dcfce7; color: #15803d; }
.tag-rose { background: #fee2e2; color: #b91c1c; }

.kpi-value-row {
  margin-bottom: 8px;
}

.kpi-amount {
  font-size: 24px;
  font-weight: 800;
  letter-spacing: -0.5px;
}

.kpi-progress-bar {
  width: 100%;
  height: 6px;
  background: #f1f5f9;
  border-radius: 999px;
  overflow: hidden;
  margin-bottom: 8px;
}

.kpi-progress-fill {
  height: 100%;
  border-radius: 999px;
  transition: width 0.4s ease;
}

.fill-emerald {
  background: linear-gradient(90deg, #34d399, #10b981);
}

.fill-rose {
  background: linear-gradient(90deg, #fb7185, #f43f5e);
}

.kpi-meta-text {
  font-size: 12px;
  color: #64748b;
  margin-top: auto;
}

/* 3. Strip 7 Trạng Thái */
.status-summary-strip {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 16px 20px;
  margin-bottom: 24px;
}

.strip-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px dashed #e2e8f0;
}

.strip-title {
  font-size: 13.5px;
  font-weight: 700;
  color: #334155;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.strip-count-text {
  font-size: 12.5px;
  color: #64748b;
}

.status-chips-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 10px;
}

.status-chip-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 10px 12px;
  transition: all 0.15s ease;
}

.status-chip-card:hover {
  border-color: #cbd5e1;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.04);
}

.chip-top {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
}

.status-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
}

.dot-gray { background: #94a3b8; }
.dot-rose { background: #f43f5e; }
.dot-amber { background: #f59e0b; }
.dot-emerald { background: #10b981; }
.dot-purple { background: #a855f7; }
.dot-teal { background: #06b6d4; }
.dot-indigo { background: #6366f1; }

.text-amber { color: #d97706; }
.text-purple { color: #9333ea; }
.text-teal { color: #0891b2; }
.text-indigo { color: #4f46e5; }
.text-slate-dark { color: #334155; font-weight: 600; }

.status-name {
  font-size: 11.5px;
  font-weight: 600;
  color: #475569;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chip-count {
  font-size: 15px;
  font-weight: 800;
  color: #0f172a;
  display: flex;
  align-items: baseline;
  gap: 3px;
}

.chip-count .unit {
  font-size: 11px;
  font-weight: 500;
  color: #64748b;
}

.chip-count .pct {
  font-size: 11px;
  font-weight: 600;
  color: #94a3b8;
  margin-left: auto;
}

/* 4. Charts */
.charts-row {
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 24px;
}

.charts-row::before,
.charts-row::after {
  display: none;
}

.charts-row > .el-col {
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
}

.chart-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100%;
  box-sizing: border-box;
}

.chart-card-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}

.chart-title {
  margin: 0 0 4px;
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
}

.chart-subtitle {
  margin: 0;
  font-size: 12px;
  color: #64748b;
}

.tuition-chart-legend {
  display: flex;
  gap: 12px;
  align-items: center;
}

.tuition-legend-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #475569;
}

.tuition-legend-color {
  width: 10px;
  height: 10px;
  border-radius: 3px;
  flex-shrink: 0;
}

.tuition-legend-color--paid { background: #10b981; }
.tuition-legend-color--remaining { background: #f43f5e; }

.badge-soft--blue {
  background: #eff6ff;
  color: #2563eb;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
}

.chart-container {
  height: 250px;
  width: 100%;
}

/* 5. Class Tuition Table */
.class-tuition-section {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 20px;
}

.class-tuition-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 14px;
}

.class-tuition-head.is-expanded {
  margin-bottom: 16px;
}

.class-tuition-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.table-toggle-btn {
  font-weight: 600;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.class-tuition-title-group {
  display: flex;
  align-items: center;
  gap: 12px;
}

.section-heading {
  margin: 0 0 3px;
  font-size: 15.5px;
  font-weight: 800;
  color: #0f172a;
}

.section-subheading {
  margin: 0;
  font-size: 12px;
  color: #64748b;
}

.class-tuition-search-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
}

.table-search-input {
  width: 220px;
}

.counter-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 700;
  color: #4f46e5;
  background: #e0e7ff;
  padding: 5px 12px;
  border-radius: 8px;
  white-space: nowrap;
}

.class-tuition-table-wrap {
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
}

.modern-tuition-table {
  width: 100%;
}

.class-cell-meta {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.class-name-txt {
  font-size: 13px;
  color: #0f172a;
}

.grade-pill {
  font-size: 10.5px;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 5px;
  display: inline-block;
  width: fit-content;
}

.pill-nursery { background: #fce7f3; color: #db2777; }
.pill-kinder_1 { background: #fef3c7; color: #b45309; }
.pill-kinder_2 { background: #dcfce7; color: #15803d; }
.pill-kinder_3 { background: #ede9fe; color: #7c3aed; }

.student-count-badge {
  display: inline-block;
  font-weight: 700;
  background: #f1f5f9;
  color: #1e293b;
  padding: 2px 7px;
  border-radius: 6px;
  font-size: 11.5px;
}

.badge-st {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
}

.badge-zero {
  color: #94a3b8;
  background: #f8fafc;
}

.badge-unbilled { background: #f1f5f9; color: #475569; }
.badge-unpaid { background: #fee2e2; color: #b91c1c; }
.badge-partial { background: #fef3c7; color: #b45309; }
.badge-fully-paid { background: #dcfce7; color: #15803d; }
.badge-overpaid { background: #f3e8ff; color: #7e22ce; }
.badge-surplus { background: #cffafe; color: #0e7490; }
.badge-deficit { background: #e0e7ff; color: #4338ca; }

.money-cell {
  font-size: 12.5px;
  font-family: inherit;
  white-space: nowrap;
}

/* Responsive */
@media (max-width: 1300px) {
  .status-chips-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}

@media (max-width: 992px) {
  .tuition-kpi-grid {
    grid-template-columns: 1fr;
  }
  .status-chips-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .stat-section-header {
    flex-direction: column;
    align-items: flex-start;
  }
  .class-tuition-head {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
